import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { useInjection } from '@core/providers/inversify-react.provider'
import { WorkspaceSwitcherPresenter } from './workspace-switcher.presenter'

/**
 * Reads WorkspaceSwitcherPresenter.viewModel and nothing else. Renders nothing
 * when `vm.visible` is false (fewer than two memberships, or an external
 * session).
 */
export const WorkspaceSwitcherComponent = observer(() => {
  const presenter: WorkspaceSwitcherPresenter = useInjection(WorkspaceSwitcherPresenter)
  const [open, setOpen] = useState(false)
  const vm = presenter.viewModel

  if (!vm.visible) return null

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-x-1 rounded-md px-2 py-1 text-shell-ink-muted hover:bg-shell-surface-muted hover:text-shell-ink"
        aria-label={vm.label}
        title={vm.label}
      >
        <span className="max-w-40 truncate text-sm font-semibold text-shell-ink">{vm.activeName}</span>
        <span className="hidden text-xs text-shell-ink-muted sm:inline">{vm.activeRoleLabel}</span>
        <ChevronDownIcon aria-hidden="true" className="size-4" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            role="group"
            aria-label={vm.label}
            className="absolute right-0 top-full z-20 mt-2.5 w-max min-w-52 origin-top-right whitespace-nowrap rounded-md bg-shell-surface py-2 shadow-lg outline-1 outline-shell-border"
          >
            {vm.options.map((option) => (
              <button
                key={option.workspaceId}
                type="button"
                onClick={() => {
                  void presenter.switchTo(option.workspaceId)
                  setOpen(false)
                }}
                aria-pressed={option.isActive}
                className={
                  option.isActive
                    ? 'mx-2 flex w-[calc(100%-1rem)] items-center justify-between gap-x-3 rounded-md px-3 py-1 text-left text-sm/6 font-semibold text-shell-accent bg-shell-surface-muted'
                    : 'mx-2 flex w-[calc(100%-1rem)] items-center justify-between gap-x-3 rounded-md px-3 py-1 text-left text-sm/6 text-shell-ink hover:bg-shell-surface-muted'
                }
              >
                <span className="truncate">{option.name}</span>
                <span className="shrink-0 text-xs text-shell-ink-muted">{option.roleLabel}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
})
